import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Upload, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const POASection = ({ 
  rating, 
  poaData = {}, 
  onPOAChange, 
  inspectionPoint = '', 
  readOnly = false,
  token
}) => {
  const [uploading, setUploading] = useState(false);
  
  const getPoaRequirement = () => {
    if (rating <= 2) return 'mandatory';
    if (rating === 3) return 'recommended';
    return 'not_required';
  };

  const poaRequirement = getPoaRequirement();

  if (poaRequirement === 'not_required') {
    return null;
  }

  const handleChange = (field, value) => {
    if (readOnly) return;
    const updated = { ...poaData, [field]: value };
    onPOAChange(updated);
  };

  const handlePhotoUpload = async (e) => {
    if (readOnly) return;
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/cleaning-inspections/upload-photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          uploadedUrls.push(response.data.url);
        }
      }

      const currentPhotos = poaData.photos || [];
      handleChange('photos', [...currentPhotos, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} photo(s) uploaded successfully`);
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    if (readOnly) return;
    const updatedPhotos = [...(poaData.photos || [])];
    updatedPhotos.splice(index, 1);
    handleChange('photos', updatedPhotos);
  };

  if (poaRequirement === 'recommended') {
    return (
      <div className="mt-4 p-4 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <Label className="text-sm font-semibold text-yellow-800">
            Opportunity for Improvement (Optional) / Oportunidad de Mejora (Opcional)
          </Label>
        </div>
        {readOnly ? (
          <p className="text-sm text-gray-700">{poaData.opportunity_for_improvement || 'None provided'}</p>
        ) : (
          <Textarea
            value={poaData.opportunity_for_improvement || ''}
            onChange={(e) => handleChange('opportunity_for_improvement', e.target.value)}
            placeholder="Describe any areas for potential improvement... / Describa cualquier área de posible mejora..."
            rows={3}
            className="text-sm"
          />
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 border-2 border-red-500 bg-red-50 rounded-lg">
      <Alert className="mb-4 bg-red-100 border-red-300">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800 font-semibold">
          ⚠️ PLAN OF ACTION REQUIRED / SE REQUIERE PLAN DE ACCIÓN
          <br />
          <span className="text-sm font-normal">
            This rating requires corrective action documentation.
          </span>
        </AlertDescription>
      </Alert>

      {inspectionPoint && (
        <div className="mb-3 p-2 bg-white rounded border">
          <Label className="text-xs text-gray-500">Inspection Point</Label>
          <p className="font-semibold">{inspectionPoint}</p>
        </div>
      )}

      <div className="mb-4">
        <Label htmlFor="deficiency">
          Deficiency Description / Descripción de Deficiencia <span className="text-red-500">*</span>
        </Label>
        {readOnly ? (
          <p className="mt-1 p-2 bg-white rounded border">{poaData.deficiency_description || 'N/A'}</p>
        ) : (
          <Textarea
            id="deficiency"
            value={poaData.deficiency_description || ''}
            onChange={(e) => handleChange('deficiency_description', e.target.value)}
            placeholder="Clearly describe what is deficient or unsatisfactory..."
            rows={3}
            required
            className="mt-1"
          />
        )}
      </div>

      <div className="mb-4">
        <Label htmlFor="corrective_action">
          Corrective Action / Acción Correctiva <span className="text-red-500">*</span>
        </Label>
        {readOnly ? (
          <p className="mt-1 p-2 bg-white rounded border">{poaData.corrective_action || 'N/A'}</p>
        ) : (
          <Textarea
            id="corrective_action"
            value={poaData.corrective_action || ''}
            onChange={(e) => handleChange('corrective_action', e.target.value)}
            placeholder="Describe the action that will be taken to correct this deficiency..."
            rows={3}
            required
            className="mt-1"
          />
        )}
      </div>

      <div className="mb-4">
        <Label htmlFor="responsible_person">
          Person Responsible / Persona Responsable <span className="text-red-500">*</span>
        </Label>
        {readOnly ? (
          <p className="mt-1 p-2 bg-white rounded border">{poaData.responsible_person || 'N/A'}</p>
        ) : (
          <Input
            id="responsible_person"
            value={poaData.responsible_person || ''}
            onChange={(e) => handleChange('responsible_person', e.target.value)}
            placeholder="Name of person responsible for correction"
            required
            className="mt-1"
          />
        )}
      </div>

      <div className="mb-4">
        <Label htmlFor="correction_date">
          Estimated Correction Date / Fecha Estimada de Corrección <span className="text-red-500">*</span>
        </Label>
        {readOnly ? (
          <p className="mt-1 p-2 bg-white rounded border">{poaData.estimated_correction_date || 'N/A'}</p>
        ) : (
          <Input
            id="correction_date"
            type="date"
            value={poaData.estimated_correction_date || ''}
            onChange={(e) => handleChange('estimated_correction_date', e.target.value)}
            required
            className="mt-1"
          />
        )}
      </div>

      <div className="mb-4">
        <Label>
          Deficiency Photos / Fotos de Deficiencia <span className="text-red-500">* At least 1 required</span>
        </Label>
        
        {!readOnly && (
          <div className="mt-2">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              id="poa-photo-upload"
              disabled={uploading}
            />
            <label htmlFor="poa-photo-upload">
              <Button 
                type="button" 
                variant="outline" 
                className="w-full" 
                disabled={uploading}
                asChild
              >
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload Photos / Subir Fotos'}
                </span>
              </Button>
            </label>
          </div>
        )}

        {poaData.photos && poaData.photos.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {poaData.photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img 
                  src={photo} 
                  alt={`Deficiency ${index + 1}`} 
                  className="w-full h-24 object-cover rounded border"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {(!poaData.photos || poaData.photos.length === 0) && (
          <Alert className="mt-2 bg-yellow-50 border-yellow-300">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-sm text-yellow-800">
              At least one photo is required to document the deficiency.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default POASection;
